import classNames from "classnames";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { LuInbox, LuList, LuPlus, LuX } from "react-icons/lu";

import { ListPager } from "../../../components/pagination/ListPager";
import { getInitialsFromFullName } from "../../../constants/constants";
import { useListMemory, useRememberList } from "../../../hooks/useListMemory";
import { useWorkFilterOptions } from "../../../hooks/useWorkFilterOptions";
import { getLinkedFirm } from "../../../sessionStorage/sessionStorage";
import { ME } from "../../clientTeam/clientTeam.utils";
import { ClientTeamInfoButton } from "../../clientTeam/ClientTeamParts";
import { WorksArchiveConfirmModal } from "../components/WorksArchiveConfirmModal";
import { WorksDeleteConfirmModal } from "../components/WorksDeleteConfirmModal";
import { AddWorkModal } from "../modals/AddWork.modal";
import { BoardWorkTable } from "./BoardWorkTable";
import { FilterMenu, FilterMenuOption } from "./FilterMenu";
import { PAGE_SIZE, useWorksBoard } from "./useWorksBoard";
import { WorkBulkBar } from "./WorkBulkActions";
import {
  clientOptionDetail,
  StatFilter,
  statusMenuItems,
  WorksTab,
} from "./worksBoard.utils";

const STAT_CARDS: {
  key: StatFilter;
  label: string;
  border: string;
}[] = [
  { key: "total", label: "Total Active Work", border: "#6B4EFF" },
  { key: "dueWeek", label: "Due This Week", border: "#C99A08" },
  { key: "overdue", label: "Overdue", border: "#E5484D" },
  { key: "later", label: "Due later", border: "#6B4EFF" },
];

const AVATAR_COLORS: Record<string, string> = {
  purple: "#7C3AED",
  blue: "#1E3A8A",
};

const QueueEmpty: FC = () => (
  <div
    className={
      "flex flex-col items-center justify-center gap-3.5 px-6 py-[70px] text-center"
    }>
    <span
      className={
        "inline-flex h-16 w-16 items-center justify-center rounded-full bg-[#F1EEFC] text-[#8A73F0]"
      }>
      <LuInbox size={30} strokeWidth={1.8} />
    </span>
    <div className={"flex max-w-[360px] flex-col gap-1.5"}>
      <span className={"text-[17px] font-extrabold text-[#1F2433]"}>
        The queue is empty
      </span>
      <span
        className={"text-[13.5px] font-medium leading-[1.55] text-[#8A92A3]"}>
        No work is waiting to be picked up right now. When work is assigned to a
        role but not yet owned by anyone, it will appear here.
      </span>
    </div>
  </div>
);

const NoWork: FC<{ filtered: boolean }> = ({ filtered }) => (
  <div
    className={
      "flex flex-col items-center justify-center gap-3 px-6 py-[60px] text-center"
    }>
    <span
      className={
        "inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#F1EEFC] text-[#8A73F0]"
      }>
      <LuList size={26} strokeWidth={1.8} />
    </span>
    <span className={"text-[15px] font-bold text-[#1F2433]"}>
      No work items
    </span>
    {filtered && (
      <span className={"text-[13px] font-medium text-[#8A92A3]"}>
        Try clearing the filters.
      </span>
    )}
  </div>
);

/** One tab of the Work page (V49): cards, filters, bulk actions, table. */
export const WorksBoardPage: FC<{ tab: WorksTab }> = ({ tab }) => {
  const [clientSearch, setClientSearch] = useState("");
  const { clientOptions, usersOptions, statusesOptions, workTypeOptions } =
    useWorkFilterOptions({ searchClients: clientSearch, includeUsers: true });
  const board = useWorksBoard(tab, statusesOptions);
  const isQueued = tab === "queued";
  const isArchived = tab === "archived";
  const hasStats = tab === "active" || tab === "my";

  const [isAddWorkOpen, setIsAddWorkOpen] = useState(false);
  const [isArchiveOpen, setIsArchiveOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  // Remember client names, so picked clients keep their label while searching
  // (and after coming Back from a work item, Roadmap 1851).
  const labelMemory = useListMemory<Record<string, FilterMenuOption>>({
    scope: `${getLinkedFirm()?.orgId || ""}:clients`,
    scroll: false,
  });
  const [clientNames, setClientNames] = useState<
    Record<string, FilterMenuOption>
  >(() => {
    const saved = labelMemory.restored;
    if (!saved || typeof saved !== "object") return {};
    // Only the picked clients' names are kept.
    return Object.fromEntries(
      board.filters.clientIds
        .filter((id) => saved[id]?.label)
        .map((id) => [id, saved[id]]),
    );
  });
  const pickedClientNames = useMemo(
    () =>
      Object.fromEntries(
        board.filters.clientIds
          .filter((id) => clientNames[id])
          .map((id) => [id, clientNames[id]]),
      ),
    [board.filters.clientIds, clientNames],
  );
  useRememberList(labelMemory, pickedClientNames);
  const clientMenuOptions = useMemo<FilterMenuOption[]>(
    () =>
      clientOptions.map((option) => ({
        value: String(option.value),
        label: option.label,
        detail: clientOptionDetail(option, clientOptions),
        avatar: {
          initials: String(option.avatar || ""),
          color:
            AVATAR_COLORS[String(option.avatarColor)] || AVATAR_COLORS.blue,
          square: option.avatarColor === "purple",
        },
      })),
    [clientOptions],
  );
  useEffect(() => {
    if (!clientMenuOptions.length) return;
    setClientNames((current) => ({
      ...current,
      ...Object.fromEntries(clientMenuOptions.map((o) => [o.value, o])),
    }));
  }, [clientMenuOptions]);
  const clientMenuWithPicked = useMemo(() => {
    const shown = new Set(clientMenuOptions.map((o) => o.value));
    const picked = board.filters.clientIds
      .filter((id) => !shown.has(id) && clientNames[id])
      .map((id) => clientNames[id]);
    return [...picked, ...clientMenuOptions];
  }, [clientMenuOptions, board.filters.clientIds, clientNames]);

  const userMenuOptions = useMemo<FilterMenuOption[]>(
    () =>
      usersOptions.map((option) => ({
        value: String(option.value),
        label: option.label,
        avatar: {
          initials:
            String(option.avatar || "") ||
            getInitialsFromFullName(option.label),
          color: option.avatarCustomColor || "#1E3A8A",
        },
        count: board.assigneeCounts[String(option.value)],
      })),
    [usersOptions, board.assigneeCounts],
  );
  // Client owner / Client manager pills (Mg-231): "Me" + the firm's team.
  const teamPeopleOptions = useMemo<FilterMenuOption[]>(
    () => [
      { value: ME, label: "Me", avatar: { initials: "ME", color: "#6B4EFF" } },
      ...userMenuOptions.map((option) => ({ ...option, count: undefined })),
    ],
    [userMenuOptions],
  );
  const { loadAssigneeCounts } = board;
  const loadUserCounts = useCallback(
    () => loadAssigneeCounts(usersOptions.map((o) => String(o.value))),
    [loadAssigneeCounts, usersOptions],
  );

  // Status groups: the Completed tab only offers the Completed group, the
  // others hide it (their lists never contain completed work). Groups keep
  // their order, sub-statuses A–Z, with counts (1833/1834).
  const statusMenuOptions = useMemo<FilterMenuOption[]>(
    () =>
      statusMenuItems(statusesOptions, board.statusCountWorks)
        .filter(({ group }) =>
          tab === "completed"
            ? group === "Completed"
            : isArchived || group !== "Completed",
        )
        .map((item) => ({
          value: item.label,
          label: item.label,
          dot: item.color,
          isBold: item.isGroup,
          isSub: !item.isGroup,
          count: item.count,
        })),
    [statusesOptions, board.statusCountWorks, tab, isArchived],
  );

  const workTypeMenuOptions = useMemo<FilterMenuOption[]>(
    () =>
      workTypeOptions.map((option) => ({
        value: String(option.value),
        label: option.label,
        count: board.workTypeCountMap
          ? board.workTypeCountMap.get(String(option.value).toLowerCase()) ?? 0
          : undefined,
      })),
    [workTypeOptions, board.workTypeCountMap],
  );

  const roleMenuOptions = useMemo<FilterMenuOption[]>(
    () =>
      board.roleChoices.map((role) => ({
        value: role.id,
        label: role.name,
        count: board.roleCountMap
          ? board.roleCountMap.get(role.id.toLowerCase()) ?? 0
          : undefined,
      })),
    [board.roleChoices, board.roleCountMap],
  );

  const { filters, setFilters } = board;
  const anyFilter =
    !!filters.clientIds.length ||
    (tab !== "my" && !!filters.userIds.length) ||
    !!filters.workTypeIds.length ||
    !!filters.statuses.length ||
    !!filters.ownerIds.length ||
    !!filters.managerIds.length ||
    (isQueued && !!filters.roleIds.length) ||
    (hasStats && board.stat !== "total");
  // Clear all also empties the client search box and the picked card
  // (QA batch 16, WORK-025).
  const clearAll = () => {
    board.clearFilters();
    setClientSearch("");
    if (hasStats && board.stat !== "total") board.setStat("total");
  };
  const hasSelection = board.selected.length > 0;

  return (
    <>
      {isQueued && (
        <div className={"mb-5 flex flex-wrap items-center gap-4"}>
          <div
            className={
              "flex items-center gap-[13px] rounded-xl border border-[#E8E8EC] bg-white px-5 py-4"
            }>
            <span
              className={
                "flex h-[38px] w-[38px] flex-none items-center justify-center rounded-[10px] bg-[#EBE6FD] text-[#6938EF]"
              }>
              <LuList size={19} strokeWidth={2} />
            </span>
            <div className={"min-w-0"}>
              <div
                className={
                  "text-2xl font-extrabold leading-none tracking-[-0.5px] text-[#1F2433]"
                }>
                {board.isFetching && !board.works.length ? "…" : board.total}
              </div>
              <div
                className={
                  "mt-[5px] text-[12.5px] font-semibold text-[#6F7688]"
                }>
                {board.queuedScope === "mine"
                  ? "Queued work in my roles"
                  : "Total queued work"}
              </div>
            </div>
          </div>
          <div className={"inline-flex items-center gap-[22px] px-1"}>
            {(["all", "mine"] as const).map((scope) => (
              <button
                type={"button"}
                key={scope}
                onClick={() => board.setQueuedScope(scope)}
                className={classNames(
                  "relative py-1.5 text-sm font-bold",
                  board.queuedScope === scope
                    ? "text-[#6B4EFF]"
                    : "text-[#6F7688] hover:text-[#3A4256]",
                )}>
                {scope === "all" ? "All queued" : "My queued"}
                <span
                  className={classNames(
                    "absolute -bottom-0.5 left-0 right-0 h-[2.5px] rounded-[3px]",
                    board.queuedScope === scope
                      ? "bg-[#6B4EFF]"
                      : "bg-transparent",
                  )}
                />
              </button>
            ))}
          </div>
        </div>
      )}

      {hasStats && (
        <div
          className={
            "mb-[22px] flex gap-4 max-md:!grid max-md:grid-cols-2 max-md:gap-3"
          }>
          {STAT_CARDS.map((card) => {
            const isOn = board.stat === card.key;
            const count = board.statCounts[card.key];
            return (
              <button
                type={"button"}
                key={card.key}
                // Clicking the picked card again clears it (QA batch 16, WORK-004).
                onClick={() => board.setStat(isOn ? "total" : card.key)}
                className={classNames(
                  "flex-1 rounded-[14px] border border-[#E8E8EC] px-4 py-5 text-center",
                  isOn && card.key === "total" ? "bg-[#F5F3FE]" : "bg-white",
                )}
                style={{
                  borderBottom: isOn ? `3px solid ${card.border}` : undefined,
                }}>
                <div
                  className={
                    "text-[28px] font-extrabold tracking-[-0.5px] text-[#1F2433] max-md:text-[24px]"
                  }
                  style={{
                    color:
                      isOn && (card.key === "total" || card.key === "later")
                        ? "#6B4EFF"
                        : undefined,
                  }}>
                  {count ?? "…"}
                </div>
                <div className={"mt-1 text-sm font-semibold text-[#6B7185]"}>
                  {card.label}
                </div>
              </button>
            );
          })}
        </div>
      )}

      <div className={"rounded-[14px] border border-[#E8E8EC] bg-white"}>
        <div
          className={
            "flex flex-wrap items-center gap-2.5 border-b border-[#F0F1F4] px-[22px] py-[18px]"
          }>
          {isQueued && (
            <FilterMenu
              variant={"role"}
              placeholder={"Select Role"}
              options={roleMenuOptions}
              selected={filters.roleIds}
              onChange={(roleIds) => setFilters({ roleIds })}
              emptyText={"No roles yet"}
            />
          )}
          <FilterMenu
            placeholder={"Search client"}
            options={clientMenuWithPicked}
            selected={filters.clientIds}
            onChange={(clientIds) => setFilters({ clientIds })}
            search={{ value: clientSearch, onChange: setClientSearch }}
            countNoun={"clients"}
            minWidth={190}
          />
          {!isQueued && tab !== "my" && (
            <FilterMenu
              placeholder={"Select Assignee"}
              options={userMenuOptions}
              selected={filters.userIds}
              onChange={(userIds) => setFilters({ userIds })}
              onOpen={loadUserCounts}
            />
          )}
          <FilterMenu
            placeholder={"Select Work Type"}
            options={workTypeMenuOptions}
            selected={filters.workTypeIds}
            onChange={(workTypeIds) => setFilters({ workTypeIds })}
          />
          <FilterMenu
            placeholder={"Select status"}
            options={statusMenuOptions}
            selected={filters.statuses}
            onChange={(statuses) => setFilters({ statuses })}
          />
          <FilterMenu
            placeholder={"Client owner"}
            options={teamPeopleOptions}
            selected={filters.ownerIds}
            onChange={(ownerIds) => setFilters({ ownerIds })}
          />
          <FilterMenu
            placeholder={"Client manager"}
            options={teamPeopleOptions}
            selected={filters.managerIds}
            onChange={(managerIds) => setFilters({ managerIds })}
          />
          <ClientTeamInfoButton />
          {anyFilter && (
            <button
              type={"button"}
              onClick={clearAll}
              className={
                "inline-flex items-center gap-[5px] whitespace-nowrap px-3 py-2 text-[13px] font-semibold text-[#6B4EFF]"
              }>
              <LuX size={14} strokeWidth={2.2} />
              Clear all
            </button>
          )}
          <span className={"flex-1"} />
          {hasSelection && (
            <WorkBulkBar
              count={board.selected.length}
              busy={board.isBusy}
              archived={isArchived}
              onChangeStatus={(status) => void board.changeStatus(status)}
              onArchive={() => setIsArchiveOpen(true)}
              onReactivate={() => void board.archive(false)}
              onDelete={() => setIsDeleteOpen(true)}
              onClear={() => board.setSelected([])}
            />
          )}
          {!isQueued && (
            <button
              type={"button"}
              onClick={() => setIsAddWorkOpen(true)}
              className={
                "inline-flex items-center gap-[7px] whitespace-nowrap rounded-lg bg-[#6B4EFF] px-[22px] py-2.5 text-[13.5px] font-bold text-white shadow-[0_3px_10px_rgba(107,78,255,0.3)]"
              }>
              <LuPlus size={16} strokeWidth={2.4} />
              Create Work
            </button>
          )}
        </div>

        <div
          className={classNames(
            "transition-opacity",
            board.isFetching && "opacity-60",
          )}>
          {board.isLoading ? (
            <div
              className={
                "px-6 py-[60px] text-center text-[13px] font-semibold text-[#8A90A2]"
              }>
              Loading work…
            </div>
          ) : (
            <BoardWorkTable
              works={board.works}
              queued={isQueued}
              selected={board.selected}
              onSelectedChange={board.setSelected}
              sortBy={board.sortBy}
              sortOrder={board.sortOrder}
              onSortChange={board.setSort}
              roleMembers={board.roleMembers}
              emptyState={
                isQueued && !anyFilter ? (
                  <QueueEmpty />
                ) : (
                  <NoWork filtered={anyFilter || board.stat !== "total"} />
                )
              }
            />
          )}
        </div>

        {!board.isLoading && board.total > 0 && (
          <ListPager
            page={board.page}
            pageSize={PAGE_SIZE}
            total={board.total}
            emptyLabel={"No work items"}
            onChange={board.setPage}
          />
        )}
      </div>

      {isAddWorkOpen && (
        <AddWorkModal
          isOpen={isAddWorkOpen}
          onClose={() => setIsAddWorkOpen(false)}
        />
      )}
      <WorksArchiveConfirmModal
        isOpen={isArchiveOpen}
        selectedWorks={board.selected}
        onClose={() => setIsArchiveOpen(false)}
        onConfirm={async () => {
          await board.archive(true);
          setIsArchiveOpen(false);
        }}
        isLoading={board.isBusy}
      />
      <WorksDeleteConfirmModal
        isOpen={isDeleteOpen}
        selectedWorks={board.selected}
        onClose={() => setIsDeleteOpen(false)}
        onConfirm={async () => {
          await board.remove();
          setIsDeleteOpen(false);
        }}
        isLoading={board.isBusy}
      />
    </>
  );
};
